import React from 'react';
import { Group, Modal, Paper, Stack, Text } from '@mantine/core';

interface PeriodDaysExampleModalProps {
  opened: boolean;
  onClose: () => void;
  periodDays?: number | null;
}

const PeriodDaysExampleModal: React.FC<PeriodDaysExampleModalProps> = ({
  opened,
  onClose,
  periodDays,
}) => {
  const hasBoundedPeriod = periodDays != null;
  const periodStartPercent = hasBoundedPeriod ? 24 : 3;
  const lastSwapPercent = 76;
  const tokenPoints = [
    { position: 8, address: 'Aj3…' },
    { position: 17, address: '9Ks…' },
    { position: 35, address: 'F4p…' },
    { position: 47, address: 'Dq8…' },
    { position: 59, address: '7Lm…' },
    { position: 68, address: 'Bx2…' },
  ];
  const periodName = hasBoundedPeriod ? `${periodDays} days` : 'all available history';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="How Period Days works"
      centered
      size="lg"
      overlayProps={{ opacity: 0.65, blur: 4 }}
      styles={{
        content: { backgroundColor: '#0D1117', border: '1px solid #374151' },
        header: { backgroundColor: '#0D1117', borderBottom: '1px solid #21262D' },
        title: { color: '#F9FAFB', fontWeight: 700, fontSize: 18 },
        close: { color: '#9CA3AF' },
        body: { padding: 20 },
      }}
    >
      <Stack gap="md">
        <Text size="sm" c="#CBD5E1" lh={1.55}>
          The period is anchored to each wallet&apos;s <strong>Last swap</strong>, not to the current
          time. The selected number of days is counted backwards from that token.
        </Text>

        <Paper
          p="md"
          radius="md"
          style={{ backgroundColor: '#07090E', border: '1px solid #2D3748' }}
        >
          <Group justify="space-between" mb={8} gap="xs" wrap="wrap">
            <Text size="xs" fw={600} c="#E5E7EB">Example wallet timeline</Text>
            <Group gap={12} wrap="nowrap">
              <Group gap={5} wrap="nowrap">
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4B5563' }} />
                <Text size="10px" c="#9CA3AF">outside period</Text>
              </Group>
              <Group gap={5} wrap="nowrap">
                <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#A5B4FC' }} />
                <Text size="10px" c="#C7D2FE">included</Text>
              </Group>
            </Group>
          </Group>

          <div style={{ position: 'relative', height: 94, margin: '0 8px' }}>
            <Text
              component="span"
              size="9px"
              c="#6B7280"
              style={{
                position: 'absolute',
                top: 4,
                left: `${lastSwapPercent}%`,
                right: 0,
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              no token activity
            </Text>

            <div style={{
              position: 'absolute',
              top: 31,
              left: 0,
              right: 0,
              height: 2,
              backgroundColor: '#4B5563',
            }} />
            <div style={{
              position: 'absolute',
              top: 30,
              left: `${periodStartPercent}%`,
              width: `${lastSwapPercent - periodStartPercent}%`,
              height: 4,
              borderRadius: 2,
              backgroundColor: '#818CF8',
            }} />
            <div style={{
              position: 'absolute',
              top: 23,
              left: `${periodStartPercent}%`,
              width: 2,
              height: 18,
              backgroundColor: '#818CF8',
            }} />

            {tokenPoints.map(({ position, address }) => {
              const isIncluded = position >= periodStartPercent;

              return (
                <React.Fragment key={position}>
                  <div style={{
                    position: 'absolute',
                    top: 26,
                    left: `${position}%`,
                    width: 11,
                    height: 11,
                    borderRadius: '50%',
                    backgroundColor: isIncluded ? '#A5B4FC' : '#4B5563',
                    transform: 'translateX(-50%)',
                  }} />
                  <Text
                    component="span"
                    size="8px"
                    c={isIncluded ? '#7C8497' : '#5D6472'}
                    style={{
                      position: 'absolute',
                      top: 42,
                      left: `${position}%`,
                      transform: 'translateX(-50%)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {address}
                  </Text>
                </React.Fragment>
              );
            })}

            <div style={{
              position: 'absolute',
              top: 25,
              left: `${lastSwapPercent}%`,
              width: 14,
              height: 14,
              borderRadius: '50%',
              backgroundColor: '#10B981',
              transform: 'translateX(-50%)',
            }} />
            <Text
              component="span"
              size="8px"
              c="#648578"
              style={{
                position: 'absolute',
                top: 42,
                left: `${lastSwapPercent}%`,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
              }}
            >
              K7v…
            </Text>

            <div style={{
              position: 'absolute',
              top: 25,
              right: 0,
              width: 2,
              height: 14,
              backgroundColor: '#6B7280',
            }} />

            <Text
              component="span"
              size="9px"
              c="#A5B4FC"
              style={{
                position: 'absolute',
                top: 64,
                left: `${periodStartPercent}%`,
                transform: hasBoundedPeriod ? 'translateX(-50%)' : undefined,
                whiteSpace: 'nowrap',
              }}
            >
              {hasBoundedPeriod ? `−${periodDays}d` : 'History start'}
            </Text>
            <Text
              component="span"
              size="9px"
              fw={600}
              c="#6EE7B7"
              style={{
                position: 'absolute',
                top: 64,
                left: `${lastSwapPercent}%`,
                transform: 'translateX(-50%)',
                whiteSpace: 'nowrap',
              }}
            >
              Last swap
            </Text>
            <Text
              component="span"
              size="9px"
              c="#9CA3AF"
              style={{ position: 'absolute', top: 64, right: 0 }}
            >
              Now
            </Text>
          </div>

          <Paper
            px="sm"
            py={8}
            radius="sm"
            style={{ backgroundColor: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.25)' }}
          >
            <Text size="xs" ta="center" c="#C7D2FE">
              Window: {hasBoundedPeriod ? `Last swap − ${periodName}` : 'All available history'} → Last swap
            </Text>
          </Paper>
        </Paper>

        <Stack gap={8}>
          <Text size="sm" c="#D1D5DB">
            <Text component="span" fw={700} c="#A5B4FC">1. Find the anchor. </Text>
            The newest token swap in the wallet becomes the right edge of the period.
          </Text>
          <Text size="sm" c="#D1D5DB">
            <Text component="span" fw={700} c="#A5B4FC">2. Count backwards. </Text>
            Tokens and statistics inside the selected period are included; older ones are outside it.
          </Text>
          <Text size="sm" c="#D1D5DB">
            <Text component="span" fw={700} c="#A5B4FC">3. Keep the anchor recent. </Text>
            A wallet with an old Last swap produces an equally old analysis window.
          </Text>
        </Stack>

        <Paper
          p="md"
          radius="md"
          style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(245, 158, 11, 0.05))',
            border: '1px solid rgba(245, 158, 11, 0.42)',
          }}
        >
          <Text size="sm" fw={700} c="#FBBF24" mb={5}>
            Recommended: limit Last swap activity
          </Text>
          <Text size="xs" c="#D1D5DB" lh={1.5}>
            Set <strong>Trading → Last Trade → Max</strong> (for example, <strong>24h</strong> or
            <strong> 7d</strong>, depending on your strategy). This keeps Last swap close to Now.
            Without this filter, an inactive wallet can be analysed around a period far in the past.
          </Text>
        </Paper>
      </Stack>
    </Modal>
  );
};

export default PeriodDaysExampleModal;
